import React, { useState, useEffect, useRef } from "react";

import useAuth from "@hooks/useAuth";

import { createPortal } from "react-dom";

import { times } from "lodash";
import UserMenuDropdown from "./UserMenuDropdown";

interface Props {
  collapsed: boolean;
}

export default function UserMenu({ collapsed }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuTriggerRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, bottom: 0 });

  const { user } = useAuth();

  // Calculate and update position when menu button changes or menu opens
  useEffect(() => {
    if (menuTriggerRef.current) {
      const rect = menuTriggerRef.current.getBoundingClientRect();
      const left = collapsed
        ? rect.right + window.scrollX + 5
        : rect.left + window.scrollX + 260;

      setMenuPosition({
        left: left,
        bottom: 20,
      });
    }
  }, [collapsed, menuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        menuTriggerRef.current &&
        !menuTriggerRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen, dropdownRef, menuTriggerRef]);

  const toggleDropdown = (event: React.MouseEvent) => {
    event.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const { first_name, last_name } = user;

  return (
    <div
      className="flex flex-row items-center justify-between border-t border-light_border cursor-pointer relative mt-[12px] px-[18px] py-[20px] w-full"
      onClick={toggleDropdown}
      ref={menuTriggerRef}
    >
      <div className="flex flex-row items-center">
        <div className="w-7 h-7 rounded-full flex items-center justify-center border border-dark_night_sky">
          <span className="text-[12px] text-dark_night_sky mt-[2px]">
            {first_name ? first_name[0] : ""} {last_name ? last_name[0] : ""}
          </span>
        </div>
        {!collapsed && (
          <div className="ml-2">
            <span className="text-[14px] font-normal text-dark_night_sky">
              {user.first_name} {user.last_name}
            </span>
            <div className="text-[12px] text-gray-500">{user.brand_name}</div>
          </div>
        )}
      </div>
      {!collapsed && (
        <div className="relative">
          <div className="flex flex-col gap-[4px]">
            {times(3, (index) => (
              <div
                className={"bg-dark_night_sky w-[14px] h-[1px]"}
                key={index}
              ></div>
            ))}
          </div>
        </div>
      )}
      {menuOpen &&
        document.body &&
        createPortal(
          <div
            className="fixed bg-white shadow-2xl rounded-xl border border-highlight_red z-[9999] w-[300px]"
            style={{
              left: `${menuPosition.left}px`,
              bottom: `${menuPosition.bottom}px`,
            }}
            ref={dropdownRef}
            onClick={(e) => e.stopPropagation()}
          >
            <UserMenuDropdown />
          </div>,
          document.body
        )}
    </div>
  );
}
